import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { getToken } from '@/api/endpoints';
import { AUTH_CONFIG, SOCKET_BASE_URL } from '@/config/constants';
import {
  getDefaultSocketOptions,
  NOTIFICATION_EVENTS,
  CHAT_EVENTS,
} from '@/utils/socketClient';
import { onTokenRefreshed } from '@/utils/authSession';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

/**
 * Global Socket Provider
 * Manages all Socket.IO connections at the top level
 * All socket connections are created once and shared across the app
 */
export const SocketProvider = ({ children }) => {
  // Socket instances in state so consumers re-render when sockets become ready
  const notificationSocketRef = useRef(null);
  const chatSocketRef = useRef(null);
  const [notificationSocket, setNotificationSocket] = useState(null);
  const [chatSocket, setChatSocket] = useState(null);
  
  // Connection states
  const [notificationConnected, setNotificationConnected] = useState(false);
  const [chatConnected, setChatConnected] = useState(false);
  
  // Initialization flag
  const isInitializedRef = useRef(false);

  // Initialize all sockets once
  useEffect(() => {
    if (isInitializedRef.current) {
      console.log('🔌 [SocketProvider] Sockets already initialized');
      return;
    }

    const token = getToken() || localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
    const baseURL = SOCKET_BASE_URL;

    if (!token) {
      console.warn('⚠️ [SocketProvider] No token found, skipping socket initialization');
      return;
    }

    console.log('🔌 [SocketProvider] Initializing global sockets →', baseURL);

    // ============================================
    // Notification Socket
    // ============================================
    const notifSocket = io(baseURL, getDefaultSocketOptions(token));

    notifSocket.on('connect', () => {
      console.log('🔔 [SocketProvider] Notification socket connected:', notifSocket.id);
      setNotificationConnected(true);
    });

    notifSocket.on('disconnect', (reason) => {
      console.log('🔔 [SocketProvider] Notification socket disconnected:', reason);
      setNotificationConnected(false);
    });

    notifSocket.on('connect_error', (error) => {
      console.error('❌ [SocketProvider] Notification socket error:', error.message || error);
      setNotificationConnected(false);
    });

    notifSocket.on('reconnect', (attemptNumber) => {
      console.log('🔄 [SocketProvider] Notification socket reconnected after', attemptNumber, 'attempts');
      setNotificationConnected(true);
    });

    notificationSocketRef.current = notifSocket;
    setNotificationSocket(notifSocket);

    // ============================================
    // Chat Socket
    // ============================================
    const chatSock = io(baseURL, getDefaultSocketOptions(token));

    chatSock.on('connect', () => {
      console.log('🔌 [SocketProvider] Chat socket connected:', chatSock.id);
      setChatConnected(true);
    });

    chatSock.on('disconnect', (reason) => {
      console.log('🔌 [SocketProvider] Chat socket disconnected:', reason);
      setChatConnected(false);
    });

    chatSock.on('connect_error', (error) => {
      console.error('❌ [SocketProvider] Chat socket error:', error);
      setChatConnected(false);
    });

    chatSock.on('reconnect', (attemptNumber) => {
      console.log('🔄 [SocketProvider] Chat socket reconnected after', attemptNumber, 'attempts');
      setChatConnected(true);
    });

    chatSocketRef.current = chatSock;
    setChatSocket(chatSock);

    isInitializedRef.current = true;

    return () => {
      console.log('🧹 [SocketProvider] Cleaning up...');
      // Keep sockets alive for app lifetime; disconnect on logout separately if needed
    };
  }, []);

  // Keep socket auth in sync without disconnecting live connections.
  useEffect(() => {
    const updateSocketAuth = (token) => {
      [notificationSocketRef.current, chatSocketRef.current].forEach((socket) => {
        if (!socket) return;
        socket.auth = { ...(socket.auth || {}), token };
      });
    };

    return onTokenRefreshed(updateSocketAuth);
  }, []);

  const value = {
    notificationSocket: notificationSocket ?? notificationSocketRef.current,
    chatSocket: chatSocket ?? chatSocketRef.current,
    
    notificationConnected,
    chatConnected,
    
    isNotificationConnected: () => notificationSocketRef.current?.connected || false,
    isChatConnected: () => chatSocketRef.current?.connected || false,
    
    NOTIFICATION_EVENTS,
    CHAT_EVENTS,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
